import { useMemo, useState } from 'react';
import { diffLines } from 'diff';
import { Undo2, GitCompare, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { checkout } from '../../lib/store';

function ordered(versions) {
  const kids = {};
  versions.forEach((v) => { (kids[v.parentId || 'root'] ||= []).push(v); });
  const out = [];
  const walk = (pid, d) => (kids[pid] || []).forEach((v) => { out.push({ v, d }); walk(v.id, d + 1); });
  walk('root', 0);
  return out.reverse();
}

function DiffView({ a, b }) {
  const parts = useMemo(() => diffLines(a, b), [a, b]);
  const added = parts.filter((p) => p.added).reduce((s, p) => s + p.count, 0);
  const removed = parts.filter((p) => p.removed).reduce((s, p) => s + p.count, 0);
  return (
    <>
      <div className="text-xs mb-2"><span className="text-emerald-400">+{added}</span> · <span className="text-rose-400">−{removed}</span> righe</div>
      <pre className="max-h-[60vh] overflow-auto rounded-lg bg-black/50 p-3 text-[11px] leading-5">
        {parts.map((p, i) => {
          if (!p.added && !p.removed) return <span key={i} className="text-slate-600">{p.count > 6 ? `  ··· ${p.count} righe invariate ···\n` : p.value}</span>;
          return <span key={i} className={p.added ? 'bg-emerald-500/15 text-emerald-300 block' : 'bg-rose-500/15 text-rose-300 block'}>{p.value.split('\n').filter(Boolean).map((l) => (p.added ? '+ ' : '- ') + l).join('\n')}{'\n'}</span>;
        })}
      </pre>
    </>
  );
}

export default function VersionTree({ game, updateGame, onCheckout, onClone }) {
  const [cmp, setCmp] = useState(null);
  const list = ordered(game.versions);
  const head = game.versions.find((v) => v.id === game.headId);
  return (
    <div data-testid="version-tree" className="space-y-2">
      <div className="t-label mb-3">Albero versioni · {game.versions.length} snapshot</div>
      {list.map(({ v, d }) => {
        const isHead = v.id === game.headId;
        return (
          <div key={v.id} data-testid={`version-${v.id}`} className="relative" style={{ paddingLeft: Math.min(d, 5) * 10 }}>
            <div className={`rounded-xl border p-3 transition-colors ${isHead ? 'border-cyan-400/60 bg-cyan-500/10 shadow-[0_0_20px_-8px_rgb(var(--cyan-500))]' : 'border-white/10 hover:border-violet-400/40'}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${isHead ? 'bg-cyan-400' : 'bg-violet-500/60'}`} />
                <span className="text-[11px] font-bold text-slate-100 truncate flex-1">v{game.versions.indexOf(v) + 1} · {v.label}</span>
                {isHead && <span className="text-[9px] tracking-widest text-cyan-300 flex items-center gap-1"><Check className="h-3 w-3" />HEAD</span>}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">{new Date(v.createdAt).toLocaleString('it-IT')}</div>
              {v.prompt && <div className="text-[11px] text-slate-400 t-chat mt-1 line-clamp-2">"{v.prompt}"</div>}
              <div className="flex gap-1.5 mt-2.5">
                {!isHead && <button data-testid={`version-restore-${v.id}`} onClick={() => { updateGame((g) => checkout(g, v.id)); onCheckout(); toast.success('Tornato alla versione selezionata'); }} className="flex items-center gap-1 rounded-md border border-cyan-500/30 px-2 py-1 text-[10px] text-cyan-200 hover:bg-cyan-500/10"><Undo2 className="h-3 w-3" />Torna qui</button>}
                {!isHead && <button data-testid={`version-compare-${v.id}`} onClick={() => setCmp(v)} className="flex items-center gap-1 rounded-md border border-violet-500/30 px-2 py-1 text-[10px] text-violet-200 hover:bg-violet-500/10"><GitCompare className="h-3 w-3" />Confronta</button>}
                <button data-testid={`version-clone-${v.id}`} onClick={() => onClone(`${game.title} (clone)`, v.code, game.genre)} className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[10px] text-slate-300 hover:bg-white/5"><Copy className="h-3 w-3" />Clona</button>
              </div>
            </div>
          </div>
        );
      })}
      <Dialog open={!!cmp} onOpenChange={(o) => !o && setCmp(null)}>
        <DialogContent data-testid="diff-dialog" className="bg-titan-surface border-violet-500/30 max-w-3xl">
          <DialogHeader><DialogTitle className="text-sm">Confronto: {cmp?.label} → HEAD ({head?.label})</DialogTitle></DialogHeader>
          {cmp && <DiffView a={cmp.code} b={game.code} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
